import React, { useRef, useState } from 'react';
import {
	createStaticRanges,
	DateRangePicker,
	defaultStaticRanges,
	Range
} from 'react-date-range';
import {
	addDays,
	endOfDay,
	endOfMonth,
	endOfToday,
	endOfWeek,
	format,
	isSameDay,
	startOfDay,
	startOfMonth,
	startOfWeek,
	subMonths
} from 'date-fns';

import type { ReportUserPeriod } from '@/modules/report/reportTypes';
import { ArrowLeft, ArrowRight, Chevron } from '@/resources/assets/images';
import useOnClickOutside from '@/hooks/useOnClickOutside';

import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import './DatePicker.scss';

interface SelectionRange {
	selection: ReportUserPeriod;
}
type Props = {
	reportUserPeriod: ReportUserPeriod;
	onChange: ({ startDate, endDate }: ReportUserPeriod) => void;
	wrapperClassName: string;
	minDate: any;
	maxDate: any;
	staticRanges: any;
};

export const WEEK_STARTS_MON = 1;

export const staticRangesCommunicationCenter = (createdAt: Date) =>
	createStaticRanges([
		{
			label: createdAt ? 'All Time' : 'Past Year',
			range: () => ({
				startDate: createdAt
					? startOfMonth(new Date(createdAt))
					: subMonths(new Date(), 12),
				endDate: new Date()
			}),
			isSelected: (range: Range) =>
				isSameDay(
					range.startDate as Date,
					createdAt
						? startOfMonth(new Date(createdAt))
						: subMonths(new Date(), 12)
				) && isSameDay(range.endDate as Date, new Date())
		},
		...defaultStaticRanges.slice(0, 2),
		{
			label: 'This Week',
			range: () => ({
				startDate: startOfWeek(new Date(), {
					weekStartsOn: WEEK_STARTS_MON
				}),
				endDate: endOfWeek(new Date(), {
					weekStartsOn: WEEK_STARTS_MON
				})
			}),
			isSelected: (range: Range) =>
				isSameDay(
					range.startDate as Date,
					startOfWeek(new Date(), {
						weekStartsOn: WEEK_STARTS_MON
					})
				) &&
				isSameDay(
					range.endDate as Date,
					endOfWeek(new Date(), {
						weekStartsOn: WEEK_STARTS_MON
					})
				)
		},
		{
			label: 'Last Week',
			range: () => ({
				startDate: startOfWeek(addDays(new Date(), -7), {
					weekStartsOn: WEEK_STARTS_MON
				}),
				endDate: endOfWeek(addDays(new Date(), -7), {
					weekStartsOn: WEEK_STARTS_MON
				})
			}),
			isSelected: (range: Range) =>
				isSameDay(
					range.startDate as Date,
					startOfWeek(addDays(new Date(), -7), {
						weekStartsOn: WEEK_STARTS_MON
					})
				) &&
				isSameDay(
					range.endDate as Date,
					endOfWeek(addDays(new Date(), -7), {
						weekStartsOn: WEEK_STARTS_MON
					})
				)
		},
		{
			label: 'Previous two weeks',
			range: () => ({
				startDate: startOfWeek(addDays(new Date(), -14), {
					weekStartsOn: WEEK_STARTS_MON
				}),
				endDate: endOfWeek(addDays(new Date(), -7), {
					weekStartsOn: WEEK_STARTS_MON
				})
			}),
			isSelected: (range: Range) =>
				isSameDay(
					range.startDate as Date,
					startOfWeek(addDays(new Date(), -14), {
						weekStartsOn: WEEK_STARTS_MON
					})
				) &&
				isSameDay(
					range.endDate as Date,
					endOfWeek(addDays(new Date(), -7), {
						weekStartsOn: WEEK_STARTS_MON
					})
				)
		},
		...defaultStaticRanges.slice(-2),
		{
			label: 'Previous two months',
			range: () => ({
				startDate: startOfMonth(subMonths(new Date(), 2)),
				endDate: endOfMonth(subMonths(new Date(), 1))
			}),
			isSelected: (range: Range) =>
				isSameDay(
					range.startDate as Date,
					startOfMonth(subMonths(new Date(), 2))
				) &&
				isSameDay(
					range.endDate as Date,
					endOfMonth(subMonths(new Date(), 1))
				)
		}
	]);

export const staticRangesStandard = createStaticRanges([
	...defaultStaticRanges.slice(0, 2),
	{
		label: 'This Week',
		range: () => ({
			startDate: startOfWeek(new Date(), {
				weekStartsOn: WEEK_STARTS_MON
			}),
			endDate: endOfWeek(new Date(), {
				weekStartsOn: WEEK_STARTS_MON
			})
		}),
		isSelected: (range: Range) =>
			isSameDay(
				range.startDate as Date,
				startOfWeek(new Date(), {
					weekStartsOn: WEEK_STARTS_MON
				})
			) &&
			isSameDay(
				range.endDate as Date,
				endOfWeek(new Date(), {
					weekStartsOn: WEEK_STARTS_MON
				})
			)
	},
	{
		label: 'Last Week',
		range: () => ({
			startDate: startOfWeek(addDays(new Date(), -7), {
				weekStartsOn: WEEK_STARTS_MON
			}),
			endDate: endOfWeek(addDays(new Date(), -7), {
				weekStartsOn: WEEK_STARTS_MON
			})
		}),
		isSelected: (range: Range) =>
			isSameDay(
				range.startDate as Date,
				startOfWeek(addDays(new Date(), -7), {
					weekStartsOn: WEEK_STARTS_MON
				})
			) &&
			isSameDay(
				range.endDate as Date,
				endOfWeek(addDays(new Date(), -7), {
					weekStartsOn: WEEK_STARTS_MON
				})
			)
	},
	{
		label: 'Previous two weeks',
		range: () => ({
			startDate: startOfWeek(addDays(new Date(), -14), {
				weekStartsOn: WEEK_STARTS_MON
			}),
			endDate: endOfWeek(addDays(new Date(), -7), {
				weekStartsOn: WEEK_STARTS_MON
			})
		}),
		isSelected: (range: Range) =>
			isSameDay(
				range.startDate as Date,
				startOfWeek(addDays(new Date(), -14), {
					weekStartsOn: WEEK_STARTS_MON
				})
			) &&
			isSameDay(
				range.endDate as Date,
				endOfWeek(addDays(new Date(), -7), {
					weekStartsOn: WEEK_STARTS_MON
				})
			)
	},
	...defaultStaticRanges.slice(-2),
	{
		label: 'Previous two months',
		range: () => ({
			startDate: startOfMonth(subMonths(new Date(), 2)),
			endDate: endOfMonth(subMonths(new Date(), 1))
		}),
		isSelected: (range: Range) =>
			isSameDay(
				range.startDate as Date,
				startOfMonth(subMonths(new Date(), 2))
			) &&
			isSameDay(
				range.endDate as Date,
				endOfMonth(subMonths(new Date(), 1))
			)
	}
]);

const DateSelector = ({
	reportUserPeriod,
	onChange,
	wrapperClassName,
	minDate,
	maxDate,
	staticRanges
}: Props): JSX.Element => {
	const pickerRef = useRef<HTMLDivElement>(null);
	const pickerContentRef = useRef<HTMLDivElement>(null);

	const [pickerVisible, setPickerVisible] = useState<boolean>(false);

	useOnClickOutside({
		ref: pickerRef,
		handler: () => setPickerVisible(false)
	});

	const [range, setRange] = useState<Range>({
		startDate: reportUserPeriod.startDate,
		endDate: reportUserPeriod.endDate,
		key: 'selection'
	});

	return (
		<div
			ref={pickerRef}
			className={`date-picker relative ${wrapperClassName}`}
		>
			{wrapperClassName === 'communication-center-date-picker' ? (
				<div
					role="button"
					tabIndex={-1}
					onKeyDown={() => undefined}
					onClick={() => setPickerVisible(!pickerVisible)}
					className="date-picker--trigger flex flex-row"
				>
					<span className="text-white">
						{`${format(range.startDate as Date, 'MMM d, y')} - ${format(
							range.endDate as Date,
							'MMM d, y'
						)}`}
					</span>
					<Chevron
						className={`icon ${pickerVisible ? 'flipped' : ''}`}
					/>
				</div>
			) : (
				<div>
					{wrapperClassName === 'admin-panel-date-picker' ? (
						<div
							role="button"
							tabIndex={-1}
							onKeyDown={() => undefined}
							onClick={() => setPickerVisible(!pickerVisible)}
							className="date-picker--trigger flex flex-row"
						>
							<span className="text-white">
								{`${format(range.startDate as Date, 'MMM d, y')} - ${format(
									range.endDate as Date,
									'MMM d, y'
								)}`}
							</span>
							<Chevron
								className={`icon ${pickerVisible ? 'flipped' : ''}`}
							/>
						</div>
					) : (
						<div
							role="button"
							tabIndex={-1}
							onKeyDown={() => undefined}
							onClick={() => setPickerVisible(!pickerVisible)}
							className="date-picker--trigger flex flex-row"
						>
							<ArrowLeft className="datepicker-dropdown-arrows" />
							<span className="text-white">
								{`${format(reportUserPeriod.startDate as Date, 'MMM d, y')} - ${format(
									reportUserPeriod.endDate as Date,
									'MMM d, y'
								)}`}
							</span>
							<ArrowRight className="datepicker-dropdown-arrows" />
						</div>
					)}
				</div>
			)}

			<div
				ref={pickerContentRef}
				className={`date-picker--content absolute right-0 ${wrapperClassName === 'communication-center-date-picker' ? 'comm-center' : ''} `}
			>
				{pickerVisible && (
					<DateRangePicker
						onChange={item => {
							const { selection } = item as SelectionRange;

							setRange(selection);

							onChange({
								...reportUserPeriod,
								startDate: startOfDay(selection.startDate),
								endDate: endOfDay(selection.endDate)
							});
						}}
						weekStartsOn={1}
						color="#FCAF0D"
						editableDateInputs={false}
						rangeColors={['#FCAF0D']}
						showMonthArrow
						minDate={minDate}
						maxDate={maxDate}
						dragSelectionEnabled
						moveRangeOnFirstSelection={false}
						showDateDisplay={false}
						className="date-picker--wrapper"
						showSelectionPreview={false}
						ranges={[range]}
						months={2}
						direction="horizontal"
						staticRanges={staticRanges}
						inputRanges={[]}
					/>
				)}
			</div>
		</div>
	);
};

export default DateSelector;
